import type {
  AuthorityCase,
  AuthorityPassage,
  AuthorityResearchState,
  AuthorityResearchTarget,
  ResearchPrinciple,
} from '../types';

export interface AuthorityInsight {
  principles: ResearchPrinciple[];
  authorities: AuthorityCase[];
  leadAuthorities: AuthorityCase[];
  adverseAuthorities: AuthorityCase[];
  verifiedPassages: number;
  totalPassages: number;
  sourceLabels: string[];
  covered: number;
  needsReview: number;
}

export function targetMatches(
  target: AuthorityResearchTarget,
  type: AuthorityResearchTarget['type'],
  id: string,
): boolean {
  return target.type === type && target.id === id;
}

export function principlesForTarget(
  research: AuthorityResearchState | undefined,
  type: AuthorityResearchTarget['type'],
  id: string,
): ResearchPrinciple[] {
  return (research?.principles ?? []).filter(principle =>
    principle.node_type !== 'folder' && principle.targets?.some(target => targetMatches(target, type, id)),
  );
}

export function passagesForAuthority(authority: AuthorityCase): AuthorityPassage[] {
  if (authority.passages?.length) return authority.passages;
  if (!authority.quote?.trim()) return [];
  return [{
    id: `legacy_${authority.id}`,
    quote: authority.quote,
    pin_cite: authority.pin_cite,
    verification_status: authority.verification_note?.toLowerCase().includes('attorney')
      ? 'attorney_verified' as const
      : 'unverified' as const,
  }];
}

export function authorityInsight(principles: ResearchPrinciple[]): AuthorityInsight {
  const authorities = principles.flatMap(principle => principle.cases ?? []);
  const passages = authorities.flatMap(passagesForAuthority);
  const leadAuthorities = principles.flatMap(principle => {
    const lead = principle.cases.find(authority => authority.id === principle.lead_case_id);
    return lead ? [lead] : [];
  });
  const sourceLabels = Array.from(new Set(authorities.map(authority =>
    authority.source_kind === 'downloaded_file' ? authority.file_name : authority.source_provider,
  ).filter((value): value is string => Boolean(value))));

  return {
    principles,
    authorities,
    leadAuthorities,
    adverseAuthorities: authorities.filter(authority => authority.side === 'adverse'),
    verifiedPassages: passages.filter(passage => passage.verification_status === 'attorney_verified').length,
    totalPassages: passages.length,
    sourceLabels,
    covered: principles.filter(principle => principle.coverage_status === 'covered').length,
    needsReview: principles.filter(principle => ['needs_review', 'unresolved', 'not_started'].includes(principle.coverage_status)).length,
  };
}

export function insightForTarget(
  research: AuthorityResearchState | undefined,
  type: AuthorityResearchTarget['type'],
  id: string,
): AuthorityInsight {
  return authorityInsight(principlesForTarget(research, type, id));
}

export function setPrinciplesForTarget(
  research: AuthorityResearchState,
  target: AuthorityResearchTarget,
  principleIds: Iterable<string>,
): AuthorityResearchState {
  const selected = new Set(principleIds);
  return {
    ...research,
    principles: research.principles.map(principle => {
      if (principle.node_type === 'folder') return principle;
      const existing = (principle.targets ?? []).find(current => current.type === target.type && current.id === target.id);
      const shouldLink = selected.has(principle.id);
      if ((!existing && !shouldLink) || (existing?.label === target.label && shouldLink)) return principle;
      const otherTargets = (principle.targets ?? []).filter(current => !(current.type === target.type && current.id === target.id));
      return {
        ...principle,
        targets: shouldLink ? [...otherTargets, target] : otherTargets,
        updated_at: new Date().toISOString(),
      };
    }),
  };
}

export function targetLabel(type: AuthorityResearchTarget['type']): string {
  return ({
    claim: 'Claim',
    element: 'Element',
    fact: 'Fact',
    motion: 'Motion',
    motion_argument: 'Motion argument',
    custom: 'Custom issue',
  })[type];
}
